import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaArrowRight, FaCopy, FaRegCheckCircle, FaLaptopCode, FaCheck,
  FaBrain, FaDatabase, FaSlidersH, FaRobot, FaSearchLocation
} from 'react-icons/fa';
import { useTheme } from '../ThemeContext';

export default function AIDetail() {
  useTheme();

  // Agent Archetypes Configuration
  const archetypes = {
    support: {
      name: 'Customer Support Agent',
      desc: 'Retrieves client records, updates Stripe sub billing, and returns direct user instructions.',
      prompt: 'System prompt:\nYou are a helpful Support Agent. Use tool [fetch_stripe_billing_profile] for parameter {customer_id}.\nParse if subscription is ACTIVE or PAST_DUE. If past due, generate a secure payment link.',
      query: 'Query: "Help! My checkout is failing. My ID is user_9821"',
      logPrefix: 'support_agent'
    },
    rag: {
      name: 'RAG Internal Specialist',
      desc: 'Semantic search querying against vectorized manuals, outputting citation-mapped summaries.',
      prompt: 'System prompt:\nYou are a RAG assistant. Query Pinecone namespace "hr_manuals" for query similarity.\nStrictly citation-map outputs to [Document Node #]. Do not extrapolate data.',
      query: 'Query: "What is our company remote-work safety policy?"',
      logPrefix: 'rag_analyst'
    },
    classifier: {
      name: 'Feedback Synthesizer',
      desc: 'Classifies unstructured logs, tags sentiment score, and routes to email support.',
      prompt: 'System prompt:\nYou are a sentiment classifier. Output results in exact JSON: { "sentiment": "positive"|"neutral"|"negative", "score": float, "flag_escalate": bool }.',
      query: 'Query: "The shipping has taken 14 days and the box was completely broken! Terribly angry!"',
      logPrefix: 'classifier_outpost'
    }
  };

  const [activeArch, setActiveArch] = useState('support');
  const [promptText, setPromptText] = useState(archetypes.support.prompt);
  const [queryText, setQueryText] = useState(archetypes.support.query);
  const [temperature, setTemperature] = useState(0.3);
  const [tokenCount, setTokenCount] = useState(72);
  const [copiedSpec, setCopiedSpec] = useState(false);

  // Sync state modifications
  useEffect(() => {
    setPromptText(archetypes[activeArch].prompt);
    setQueryText(archetypes[activeArch].query);
  }, [activeArch]);

  // Dynamic token calculator effect
  useEffect(() => {
    const totalChars = promptText.length + queryText.length;
    setTokenCount(Math.round(totalChars / 4.2));
  }, [promptText, queryText]);

  // Terminal Simulator log outputs
  const [runningAgent, setRunningAgent] = useState(false);
  const [agentCompleted, setAgentCompleted] = useState(false);
  const [terminalOutput, setTerminalOutput] = useState('');

  const handleRunAgent = () => {
    if (runningAgent) return;
    setRunningAgent(true);
    setAgentCompleted(false);
    setTerminalOutput(`$ python agent_orchestration.py --mode ${activeArch} --temp ${temperature}\n`);

    const prefix = archetypes[activeArch].logPrefix;

    setTimeout(() => {
      setTerminalOutput(prev => prev + `[${prefix}.init] Load Pinecone index 'bcc_vector_store' (1536 dim)... OK\n`);
    }, 400);

    setTimeout(() => {
      setTerminalOutput(prev => prev + `[${prefix}.search] Query embeddings search: cosine similarity: 0.92\n`);
    }, 800);

    setTimeout(() => {
      setTerminalOutput(prev => prev + `[${prefix}.llm] Dispatched request to Claude-3-5-Sonnet (temp=${temperature})...\n`);
    }, 1300);

    setTimeout(() => {
      // Custom summary returns
      let responseMock = '';
      if (activeArch === 'support') {
        responseMock = '{\n  "status": "PAST_DUE",\n  "escalate": true,\n  "reply": "I see your account user_9821 has a pending invoice. Please settle at https://billing.stripe.com/p/..."\n}';
      } else if (activeArch === 'rag') {
        responseMock = '"Employees can work remotely up to 3 days/week with manager consent [Ref: Manual_Sec_14]. Submit home office setup claims through HR Portal..."';
      } else {
        responseMock = '{\n  "sentiment": "negative",\n  "score": 0.98,\n  "flag_escalate": true\n}';
      }
      
      setTerminalOutput(prev => prev + `[${prefix}.done] Model response:\n${responseMock}\n\n✔ Execution completed. Total cost: $0.0054 (Tokens: ${tokenCount})`);
      setRunningAgent(false);
      setAgentCompleted(true);
    }, 2200);
  };

  const handleCopySpec = () => {
    const specSummary = `BCC AI Agent Spec:
- Archetype: ${archetypes[activeArch].name}
- System Prompt: ${promptText}
- Parameters: Temp ${temperature} | Tokens: ${tokenCount}`;
    navigator.clipboard.writeText(specSummary);
    setCopiedSpec(true);
  };

  useEffect(() => {
    if (copiedSpec) {
      const timer = setTimeout(() => setCopiedSpec(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copiedSpec]);

  // Milestones Timeline state
  const [activeMilestone, setActiveMilestone] = useState('prompt');

  return (
    <div className="min-h-screen overflow-x-hidden transition-all duration-300" style={{ background: 'var(--bg-primary)' }}>
      
      {/* ── HERO SECTION ── */}
      <section className="relative pt-32 pb-24 px-6 overflow-hidden animate-fade-in" 
               style={{ 
                 background: 'var(--bg-card)',
                 borderBottom: '1px solid var(--section-divider)' 
               }}>
        <div className="absolute top-0 right-0 w-[550px] h-[550px] rounded-full blur-[140px] opacity-10 pointer-events-none bg-indigo-500" />
        
        <div className="section-container relative z-10 text-center max-w-4xl">
          
          {/* AI Intelligence Category Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 border border-indigo-505/20 bg-indigo-500/10 shadow-sm"
               style={{ borderColor: 'var(--border-color)' }}>
            <span className="w-2.5 h-2.5 rounded bg-clip-border flex items-center justify-center shrink-0">
               <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-ping" />
            </span>
            <span className="text-xs font-black uppercase tracking-wider text-indigo-400">Next-Generation Autonomous Systems</span>
          </div>

          <h1 className="display-lg mb-6 leading-tight font-black tracking-tight" style={{ color: 'var(--text-primary)' }}>
            AI <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 to-purple-400">Intelligent Agents</span>.
          </h1>
          
          <p className="text-lg leading-relaxed mb-10 max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            We engineer retrieval systems and custom autonomous pipelines that connect proprietary vector repositories, tools orchestration databases, and LLM APIs to automate knowledge work.
          </p>

          <div className="flex justify-center gap-4">
            <Link to="/contactus" className="btn-primary inline-flex bg-gradient-to-r from-indigo-500 to-purple-550 text-white border-none hover:opacity-90">
              Build AI Agent Spec <FaArrowRight size={11} className="ml-1.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── TECHNICAL CAPABILITIES ── */}
      <section className="py-24 px-6 border-b animate-fade-in" style={{ background: 'var(--bg-primary)', borderColor: 'var(--section-divider)' }}>
        <div className="section-container max-w-6xl">
          
          <div className="text-center mb-20">
            <p className="eyebrow mb-2">Capabilities Deck</p>
            <h2 className="display-sm mb-4" style={{ color: 'var(--text-primary)' }}>AI Core Infrastructure</h2>
            <p className="subheadline max-w-2xl mx-auto">
              Our engineering systems are designed to bridge raw client data to production endpoints with sub-second retrieval times.
            </p>
          </div>

          <div className="space-y-28">

            {/* Row 1: RAG & Semantic Search */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="p-8 rounded-3xl border bg-gradient-to-br from-indigo-550/10 to-purple-500/5 shadow-inner" style={{ borderColor: 'var(--border-color)' }}>
                <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center text-indigo-400 mb-6">
                  <FaDatabase size={20} />
                </div>
                <h3 className="text-lg font-black mb-3 text-white">Retrieval-Augmented Generation (RAG)</h3>
                <p className="text-sm leading-relaxed text-zinc-400">
                  Connect local documentation folders, contracts, or SQL schemas. We setup automated parsing files chains to ingest chunks into high-dimension vector systems like Pinecone.
                </p>
              </div>
              <div>
                <span className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-indigo-400 mb-4">
                  <span className="w-4 h-px bg-indigo-400 inline-block" /> Vector Search
                </span>
                <h3 className="text-3xl font-black mb-4 leading-tight" style={{ color: 'var(--text-primary)' }}>
                  Semantic Search Repositories
                </h3>
                <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>
                  We index unstructured business documentation so models cite exact page contexts instead of guessing answers.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <FaCheck size={10} className="text-indigo-500 mt-1 shrink-0" />
                    Automated chunking and sentence embedding pipelines.
                  </li>
                  <li className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <FaCheck size={10} className="text-indigo-500 mt-1 shrink-0" />
                    Zero Hallucination retrieval thresholds configurations.
                  </li>
                </ul>
              </div>
            </div>

            {/* Row 2: Tool Routing */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <span className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-purple-400 mb-4">
                  <span className="w-4 h-px bg-purple-400 inline-block" /> Agent Frameworks
                </span>
                <h3 className="text-3xl font-black mb-4 leading-tight" style={{ color: 'var(--text-primary)' }}>
                  Autonomous Tool Execution
                </h3>
                <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>
                  Go beyond text. We construct agents equipped with custom API schema descriptors, allowing models to query Stripe profiles or update CRM files automatically based on context commands.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <FaCheck size={10} className="text-purple-500 mt-1 shrink-0" />
                    Secure API sandbox environments routing logs.
                  </li>
                  <li className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <FaCheck size={10} className="text-purple-500 mt-1 shrink-0" />
                    Dynamic fallback states handling unexpected model schemas.
                  </li>
                </ul>
              </div>
              <div className="order-1 lg:order-2 p-8 rounded-3xl border bg-gradient-to-br from-purple-500/10 to-indigo-550/5 shadow-inner" style={{ borderColor: 'var(--border-color)' }}>
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center text-purple-400 mb-6">
                  <FaRobot size={20} />
                </div>
                <h3 className="text-lg font-black mb-3 text-white">Custom Action Engines</h3>
                <p className="text-sm leading-relaxed text-zinc-400">
                  Allow neural models to route workflows. Build automated email responders, booking validators, and ticket triage monitors.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── THE INTERACTIVE PROMPT CONSOLE BUILDER ── */}
      <section className="py-24 px-6 border-b" style={{ background: 'var(--bg-surface)', borderColor: 'var(--section-divider)' }}>
        <div className="section-container max-w-5xl">
          
          <div className="text-center mb-16">
            <p className="eyebrow mb-2">Prompt Engineer Sandbox</p>
            <h2 className="display-sm mb-4" style={{ color: 'var(--text-primary)' }}>Interactive Agent Playground</h2>
            <p className="subheadline max-w-2xl mx-auto">
              Select an agent archetype, modify parameters, and run automated vector search executions in the browser window simulation.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            
            {/* Left Controls (5 columns) */}
            <div className="lg:col-span-5 flex flex-col justify-between border rounded-3xl p-6 shadow-xl"
                 style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
              <div>
                
                {/* Selector */}
                <h3 className="text-xs font-black uppercase tracking-wider mb-3 text-indigo-400">1. Select Agent Archetype</h3>
                <div className="space-y-2 mb-6">
                  {Object.keys(archetypes).map(key => (
                    <button
                      key={key}
                      onClick={() => setActiveArch(key)}
                      className={`w-full text-left p-3 rounded-xl border flex items-center gap-3 transition-all ${
                        activeArch === key 
                          ? 'bg-indigo-500/10 border-indigo-500/40 text-indigo-400' 
                          : 'border-zinc-800/10 dark:border-zinc-700/10 hover:bg-zinc-800/5'
                      }`}
                    >
                      <div className="w-2.5 h-2.5 rounded-full bg-indigo-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h4 className="text-[10px] font-black uppercase text-white leading-none">{archetypes[key].name}</h4>
                        <p className="text-[8px] text-zinc-550 truncate mt-1">{archetypes[key].desc}</p>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Prompt parameters */}
                <h3 className="text-xs font-black uppercase tracking-wider mb-2.5 text-indigo-400">2. System Parameters</h3>
                <div className="space-y-4 mb-6">
                  
                  {/* Temperature Slider */}
                  <div className="p-3 bg-zinc-950/60 rounded-xl border border-zinc-850">
                    <div className="flex justify-between text-[9px] mb-1.5 font-mono">
                      <span className="text-zinc-555">LLM TEMPERATURE</span>
                      <span className="text-indigo-400 font-bold">{temperature}</span>
                    </div>
                    <input
                      type="range"
                      min="0.0"
                      max="1.0"
                      step="0.1"
                      value={temperature}
                      onChange={(e) => setTemperature(parseFloat(e.target.value))}
                      className="w-full h-1 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-indigo-500"
                    />
                  </div>

                  {/* System Prompt Viewport */}
                  <div className="p-3 bg-zinc-950/60 rounded-xl border border-zinc-850">
                    <span className="text-[9px] text-zinc-555 uppercase font-mono block mb-1">Prompt Context Configurations</span>
                    <textarea
                      value={promptText}
                      onChange={(e) => setPromptText(e.target.value)}
                      rows={4}
                      className="w-full text-[9px] py-1 px-2 rounded-lg bg-zinc-950 text-indigo-300 font-mono border border-zinc-850 focus:border-indigo-500/50 outline-none resize-none"
                    />
                  </div>

                </div>

              </div>

              {/* Action buttons */}
              <div className="mt-6 flex flex-col gap-2">
                <button
                  onClick={handleRunAgent}
                  disabled={runningAgent}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 hover:opacity-90 text-white font-black text-xs tracking-wider transition-all"
                >
                  <FaRegCheckCircle size={12} />
                  {runningAgent ? "EXECUTING Claude INBOUND..." : "RUN INTERACTIVE PROMPT"}
                </button>

                <button
                  onClick={handleCopySpec}
                  className="w-full flex items-center justify-center gap-1.5 py-2 px-4 rounded-lg bg-zinc-900 border border-zinc-850 text-zinc-400 font-mono text-[9px] hover:bg-zinc-800 transition-all"
                >
                  {copiedSpec ? "SPEC COPIED!" : "COPY SPEC FOR QUOTE"}
                </button>
              </div>

            </div>

            {/* Right Screen Mock (7 columns) */}
            <div className="lg:col-span-7 flex flex-col justify-stretch">
              <div className="w-full h-full border rounded-3xl p-6 flex flex-col justify-between shadow-xl"
                   style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
                
                <div>
                  <div className="flex items-center justify-between pb-3 border-b mb-6" style={{ borderColor: 'var(--border-color)' }}>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-indigo-500 animate-pulse" />
                      <span className="text-[10px] font-mono uppercase font-bold tracking-widest text-zinc-400">
                        Agent Orchestration Outpost
                      </span>
                    </div>
                    <span className="text-[8.5px] uppercase font-mono font-black text-indigo-400" >
                      Sonnet-3.5 Active
                    </span>
                  </div>

                  {/* Browser Mock Wrapper */}
                  <div className="w-full border rounded-2xl overflow-hidden relative bg-zinc-950/45 border-zinc-850" style={{ backdropFilter: 'blur(10px)' }}>
                    
                    {/* URL Bar */}
                    <div className="bg-zinc-900 px-4 py-2 border-b border-zinc-850 flex items-center justify-between">
                      <div className="flex gap-1 items-center shrink-0">
                        <span className="w-1.5 h-1.5 rounded-full bg-zinc-700" />
                        <span className="w-1.5 h-1.5 rounded-full bg-zinc-700" />
                        <span className="w-1.5 h-1.5 rounded-full bg-zinc-700" />
                      </div>
                      
                      <div className="flex-grow max-w-sm mx-auto h-5 bg-zinc-950 rounded text-[9px] text-zinc-500 flex items-center justify-between px-2 font-mono border border-zinc-850">
                        <span>Workspace: {archetypes[activeArch].name.toLowerCase().replace(/ /g, '_')}</span>
                        <span className="text-[8px] font-bold text-indigo-400 uppercase">Live Sandbox</span>
                      </div>

                      <div className="w-4" />
                    </div>

                    {/* Page Content Dashboard */}
                    <div className="p-4 space-y-3.5 text-left h-[260px] overflow-y-auto">
                      
                      {/* Embeddings input display */}
                      <span className="text-[8px] uppercase font-mono font-black text-indigo-400 tracking-widest block border-b border-zinc-850 pb-1.5">Inbound Query Payload</span>

                      <div className="p-2.5 rounded-lg border border-zinc-850 bg-zinc-900/60 text-[9px] font-mono text-white leading-relaxed">
                        {queryText}
                      </div>

                      {/* Diagnostic Logs Terminal block */}
                      <div className="p-2.5 rounded-xl border border-zinc-850 bg-zinc-950 font-mono text-[7.5px] min-h-[120px]">
                        <span className="text-[7.5px] uppercase font-bold text-zinc-500 block mb-1.5 tracking-wider border-b border-zinc-850 pb-1">Telemetry Diagnostics</span>
                        <div className="space-y-1 h-[85px] overflow-y-auto text-zinc-400 whitespace-pre-wrap leading-relaxed">
                          {terminalOutput ? (
                            terminalOutput
                          ) : (
                            <span className="text-zinc-600 block text-center pt-6">Awaiting prompt execution telemetry logs...</span>
                          )}
                        </div>
                      </div>

                      <div className="flex justify-between items-center text-[8.5px] font-mono text-zinc-500">
                        <span>Prompt size: {tokenCount} tokens</span>
                        <span>Estimated cost: ${(tokenCount * 0.000015).toFixed(5)}</span>
                      </div>

                    </div>

                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── INTERACTIVE CALIBRATION MILESTONES TIMELINE ── */}
      <section className="py-24 px-6 border-b" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
        <div className="section-container max-w-4xl mx-auto">
          
          <div className="text-center mb-16">
            <p className="eyebrow mb-2">Setup Workflow</p>
            <h2 className="display-sm mb-4" style={{ color: 'var(--text-primary)' }}>AI Agent Tuning Milestones</h2>
            <p className="subheadline max-w-2xl mx-auto">
              Follow our calibration sprints. Click on a checkpoint to view workflow instructions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-stretch">
            
            {/* Milestones selector (5 columns) */}
            <div className="md:col-span-5 space-y-3">
              {[
                { id: 'prompt', step: '01', title: 'Context & Prompt Maps', desc: 'Lock system parameters and baseline prompt boundaries.' },
                { id: 'vector', step: '02', title: 'Vector Datasets Ingestion', desc: 'Chunk databases files, run embeddings, push to servers.' },
                { id: 'tools', step: '03', title: 'APIs Functional Routing', desc: 'Secure agent access keys to external webhook portals.' },
                { id: 'testing', step: '04', title: 'Sandbox Evaluator Audit', desc: 'Run automated similarity checks on prompt inputs.' }
              ].map(s => (
                <button
                  key={s.id}
                  onClick={() => setActiveMilestone(s.id)}
                  className={`w-full text-left p-3.5 rounded-2xl border flex items-center gap-4 transition-all duration-200 ${
                    activeMilestone === s.id 
                      ? 'bg-indigo-500/10 border-indigo-500/40 text-indigo-400 scale-[1.01]' 
                      : 'border-zinc-800/10 dark:border-zinc-700/10 hover:bg-zinc-800/5'
                  }`}
                >
                  <span className={`text-md font-mono font-black ${activeMilestone === s.id ? 'text-indigo-400' : 'text-zinc-550'}`}>
                    {s.step}
                  </span>
                  <div>
                    <h4 className="text-xs font-black uppercase text-white leading-none mb-1">{s.title}</h4>
                    <p className="text-[9.5px] text-zinc-450">{s.desc}</p>
                  </div>
                </button>
              ))}
            </div>

            {/* Instruction view deck (7 columns) */}
            <div className="md:col-span-7 flex flex-col justify-stretch">
              <div className="w-full h-full border rounded-2xl overflow-hidden bg-zinc-950 border-zinc-850 p-6 font-mono shadow-2xl flex flex-col justify-between">
                <div>
                  <div className="flex justify-between items-center pb-2.5 border-b border-zinc-850 mb-4 text-zinc-500 text-[10px]">
                    <span className="uppercase tracking-widest font-bold">Workspace : pipeline_docs</span>
                    <span className="text-[9px] text-indigo-400">Step ACTIVE</span>
                  </div>

                  <div className="text-left text-zinc-350 text-[10px] space-y-4">
                    {activeMilestone === 'prompt' && (
                      <>
                        <h4 className="text-white text-xs font-bold font-sans">Prompt calibrators definition:</h4>
                        <p className="leading-relaxed">We outline agent personality cards, strict token constraints, response format templates (e.g. JSON schemas), and target escalation channels.</p>
                      </>
                    )}
                    {activeMilestone === 'vector' && (
                      <>
                        <h4 className="text-white text-xs font-bold font-sans">Embedding configurations:</h4>
                        <p className="leading-relaxed">Ingest unstructured sources (PDFs, SQL charts). Run dynamic chunks slicing models to create 1536-dimensional embeddings vectors pushed into isolated Pinecone clouds.</p>
                      </>
                    )}
                    {activeMilestone === 'tools' && (
                      <>
                        <h4 className="text-white text-xs font-bold font-sans">Functional APIs access maps:</h4>
                        <p className="leading-relaxed">Configure sandboxed environments allowing agents to execute functions (e.g. write database lines or dispatch confirmation emails) using standard OpenAPI spec definitions.</p>
                      </>
                    )}
                    {activeMilestone === 'testing' && (
                      <>
                        <h4 className="text-white text-xs font-bold font-sans">Evaluators load tests:</h4>
                        <p className="leading-relaxed">Batch-execute simulated queries. Check token metrics, response times, model hallucinations, and cosine similarity factors under strict security policies.</p>
                      </>
                    )}
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-zinc-850 flex justify-between items-center text-[8.5px] text-zinc-500 font-sans">
                  <span>Bombay Coding Company AI Pipeline</span>
                  <span className="text-indigo-400">READY</span>
                </div>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* ── BOTTOM BACK CTA ── */}
      <section className="py-20 text-center" style={{ background: 'var(--bg-primary)' }}>
         <Link to="/services" className="text-sm font-semibold transition-colors inline-flex items-center gap-2"
               style={{ color: 'var(--accent)' }}>
            <FaArrowRight size={12} className="transform rotate-180 text-indigo-400" /> Back to All Services
         </Link>
      </section>

    </div>
  );
}
